import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useUsers from "~/api/swr/useUsers";
import { inviteQuizTakers } from "~/api/v2/requests";
import { type IdAndTitle } from "~/api/types";
import { PlatformRole, UserStatus } from "~/config/types";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import { CenterSpinner } from "~/atoms/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import MoreTooltip from "~/molecules/MoreTooltip";
import NoResult from "~/molecules/NoResult";

interface QuizToInvite {
  quizId: number;
  category: IdAndTitle | null;
}

interface Props {
  quizzes: QuizToInvite[];
  onOpenChange: (open: boolean) => void;
  onInvited: () => void;
}

/**
 * Invite users to every selected quiz in one action. Eligible users are those
 * in any selected quiz's document category, plus coordinators and superadmins.
 *
 * Mounted only while open, so the selection starts empty each time.
 */
const InviteQuizTakersModal = ({ quizzes, onOpenChange, onInvited }: Props) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const { data: users, isLoading } = useUsers({ status: [UserStatus.active] });

  const categoryIds = new Set(
    quizzes.flatMap((quiz) => (quiz.category ? [quiz.category.id] : [])),
  );
  const eligibleUsers = users?.filter(
    (user) =>
      user.categories.some((category) => categoryIds.has(category.id)) ||
      user.role === PlatformRole.coordinator ||
      user.role === PlatformRole.superAdmin,
  );

  const toggleUser = (userId: number) =>
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );

  const sendInvitations = async () => {
    if (selectedUserIds.length === 0) return;

    try {
      setIsSubmitting(true);
      await Promise.all(
        quizzes.map((quiz) => inviteQuizTakers(quiz.quizId, selectedUserIds)),
      );
      toast.success(t(($) => $.anchoring.invite.success));
      onInvited();
      onOpenChange(false);
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-250 flex-col">
        <DialogHeader>
          <DialogTitle>{t(($) => $.anchoring.invite.title)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.anchoring.invite.description, {
              count: quizzes.length,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead>{t(($) => $.commons.user.name)}</TableHead>
                <TableHead>{t(($) => $.commons.role)}</TableHead>
                <TableHead>{t(($) => $.commons.document.category)}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    <CenterSpinner />
                  </TableCell>
                </TableRow>
              )}
              {eligibleUsers?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <NoResult />
                  </TableCell>
                </TableRow>
              )}
              {eligibleUsers?.map((user) => (
                <TableRow
                  key={user.id}
                  className="cursor-pointer"
                  onClick={() => toggleUser(user.id)}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedUserIds.includes(user.id)}
                      onClick={(event) => event.stopPropagation()}
                      onCheckedChange={() => toggleUser(user.id)}
                      aria-label={t(($) => $.commons.buttons.selectItem, {
                        name: `${user.firstName} ${user.lastName}`,
                      })}
                    />
                  </TableCell>
                  <TableCell>
                    {user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell>{t(($) => $.commons.roles[user.role])}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span>{user.categories[0]?.title ?? "-"}</span>
                      <MoreTooltip
                        data={user.categories.map((category) => category.title)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button
            onClick={sendInvitations}
            disabled={isSubmitting || selectedUserIds.length === 0}
          >
            {t(($) => $.commons.buttons.sentInvitations)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteQuizTakersModal;
